// src/utils/csvExport.ts
type CsvPrimitive = string | number | boolean | null | undefined;

export type CsvCol<T> = {
    key: keyof T;
    header: string;
    /** "num" / "rate" など。未指定なら生値を文字列化 */
    type?: "num" | "rate" | "text";
};

export type CsvExportOptions = {
    /** Excel向けにUTF-8 BOMを先頭に付ける（既定: true） */
    withBom?: boolean;
    /** 改行（既定: "\r\n"） */
    newline?: "\n" | "\r\n";
    /** rate の扱い（既定: "auto"）
     *  - "auto": |v|<=1.5 なら 0-1 とみなして%化、それ以外はそのまま%扱い
     *  - "0-1": 常に v*100 を%表示
     *  - "0-100": 常に v を%表示（12.3 -> 12.3%）
     */
    rateMode?: "auto" | "0-1" | "0-100";
    /** rate の小数点桁数（既定: 2） */
    rateDigits?: number;
};

function csvEscape(value: string): string {
    // カンマ・改行・ダブルクォートを含む場合はダブルクォートで囲み、内部の"は""にする
    if (/[",\r\n]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

function formatCell(value: CsvPrimitive, type: CsvCol<any>["type"], opt: Required<CsvExportOptions>): string {
    if (value === null || value === undefined) return "";

    if (type === "num") {
        // 数値はそのまま（桁区切りしない：Excelで数値として扱わせる）
        return typeof value === "number" ? String(value) : String(value);
    }

    if (type === "rate") {
        const n = typeof value === "number" ? value : Number(value);
        if (!Number.isFinite(n)) return "";

        let pct = n;
        const abs = Math.abs(n);

        if (opt.rateMode === "0-1") pct = n * 100;
        else if (opt.rateMode === "0-100") pct = n;
        else {
            // auto
            pct = abs <= 1.5 ? n * 100 : n;
        }

        // 例: 12.34%
        return `${pct.toFixed(opt.rateDigits)}%`;
    }

    // text or default
    return String(value);
}

function buildCsv<T extends Record<string, any>>(
    rows: readonly T[],
    cols: readonly CsvCol<T>[],
    options?: CsvExportOptions
): string {
    const opt: Required<CsvExportOptions> = {
        withBom: options?.withBom ?? true,
        newline: options?.newline ?? "\r\n",
        rateMode: options?.rateMode ?? "auto",
        rateDigits: options?.rateDigits ?? 2,
    };

    const header = cols.map(c => csvEscape(String(c.header))).join(",");

    const body = rows
        .map(r => {
            const line = cols
                .map(c => {
                    const raw = r[c.key as string] as CsvPrimitive;
                    const formatted = formatCell(raw, c.type, opt);
                    return csvEscape(formatted);
                })
                .join(",");
            return line;
        })
        .join(opt.newline);

    const csv = header + opt.newline + body;
    return opt.withBom ? "\uFEFF" + csv : csv;
}

export function downloadCsv<T extends Record<string, any>>(
    filename: string,
    rows: readonly T[],
    cols: readonly CsvCol<T>[],
    options?: CsvExportOptions
) {
    const csvText = buildCsv(rows, cols, options);
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
}
